/**
 * TwitterAdMockup Component
 * Displays a mockup of a Twitter/X promoted tweet ad
 *
 * Twitter ads (promoted tweets) appear in user timelines, search results, and profiles.
 * They look like regular tweets but have a "Promoted" label.
 *
 * Features:
 * - Promoted badge at top
 * - Tweet text content
 * - Media (image or video) support
 * - Call-to-action button
 * - Engagement metrics (likes, retweets, replies)
 * - Brand profile information
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';

export interface TwitterAdMockupProps {
  imageUrl?: string;
  videoUrl?: string;
  tweetText: string;
  callToAction?: string;
  brandName?: string;
  brandHandle?: string;
  brandLogo?: string;
  linkUrl?: string;
  verified?: boolean;
}

export const TwitterAdMockup: React.FC<TwitterAdMockupProps> = ({
  imageUrl,
  videoUrl,
  tweetText,
  callToAction = 'Learn more',
  brandName = 'Your Brand',
  brandHandle = '@yourbrand',
  brandLogo,
  linkUrl,
  verified = false,
}) => {
  const mediaUrl = videoUrl || imageUrl;

  return (
    <div className="bg-white dark:bg-background-dark rounded-lg border border-gray-200 dark:border-border-dark shadow-sm max-w-[600px] mx-auto">
      {/* Header - Brand Info */}
      <div className="p-4 pb-2">
        <div className="flex items-start gap-3">
          {/* Brand Logo */}
          <div className="w-12 h-12 rounded-full bg-gray-200 dark:bg-white/10 flex-shrink-0 overflow-hidden">
            {brandLogo ? (
              <img src={brandLogo} alt={brandName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500 dark:text-gray-400 text-lg font-bold">
                {brandName.charAt(0).toUpperCase()}
              </div>
            )}
          </div>

          {/* Brand Name and Handle */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1">
              <span className="font-bold text-gray-900 dark:text-white truncate">
                {brandName}
              </span>
              {verified && (
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                </svg>
              )}
              <span className="text-gray-500 dark:text-gray-400 truncate">
                {brandHandle}
              </span>
            </div>
            {/* Promoted Label */}
            <div className="flex items-center gap-1 mt-0.5">
              <span className="text-gray-500 dark:text-gray-400 text-sm">Promoted</span>
            </div>
          </div>

          {/* More Options Icon */}
          <button className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <circle cx="12" cy="5" r="2"/>
              <circle cx="12" cy="12" r="2"/>
              <circle cx="12" cy="19" r="2"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Tweet Text */}
      <div className="px-4 pb-3">
        <p className="text-gray-900 dark:text-white text-base leading-normal whitespace-pre-wrap">
          {stripMediaTags(tweetText)}
        </p>
      </div>

      {/* Media */}
      {mediaUrl && (
        <div className="relative w-full bg-gray-100 dark:bg-card-darkAlt overflow-hidden" style={{ aspectRatio: '16/9' }}>
          {videoUrl ? (
            <video
              src={videoUrl}
              className="w-full h-full object-cover"
              controls
              playsInline
            />
          ) : (
            <img
              src={imageUrl}
              alt="Ad content"
              className="w-full h-full object-cover"
            />
          )}
        </div>
      )}

      {/* Call-to-Action Button (if provided) */}
      {callToAction && linkUrl && (
        <div className="px-4 pt-3">
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full py-2 px-4 bg-blue-500 hover:bg-primary text-white text-center font-medium rounded-full transition-colors"
          >
            {callToAction}
          </a>
        </div>
      )}

      {/* Engagement Metrics */}
      <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-border-dark">
        {/* Reply */}
        <button className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors group">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="text-sm group-hover:text-blue-500 dark:group-hover:text-blue-400">Reply</span>
        </button>

        {/* Retweet */}
        <button className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-green-500 dark:hover:text-green-400 transition-colors group">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
          <span className="text-sm group-hover:text-green-500 dark:group-hover:text-green-400">Retweet</span>
        </button>

        {/* Like */}
        <button className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors group">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
          <span className="text-sm group-hover:text-red-500 dark:group-hover:text-red-400">Like</span>
        </button>

        {/* Share */}
        <button className="flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors group">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span className="text-sm group-hover:text-blue-500 dark:group-hover:text-blue-400">Share</span>
        </button>
      </div>

      {/* Footer Note */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-white/5 border-t border-gray-200 dark:border-border-dark">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          This is a preview of how your promoted tweet will appear to users on Twitter/X.
        </p>
      </div>
    </div>
  );
};

export default TwitterAdMockup;
